import Filter from '@sats-group/ui-lib/react/filter';
import Search from '@sats-group/ui-lib/react/search';
import Text from '@sats-group/ui-lib/react/text';
import FuzzySearch from 'fuzzy-search';
import React, { useState } from 'react';
import ContentContainer from '~/client/components/content-container/content-container';
import Layout from '~/client/components/layout/layout';
import LinkBoxList from '~/client/components/link-box-list/link-box-list';
import TextInterpolator from '~/client/components/text-interpolator/text-interpolator';
import debounce from '~/client/helpers/debounce';
import isNumber from '~/client/helpers/is-number';
import type { LinkBoxListPage as Props } from './link-box-list-page.types';

const LinkBoxListPage: React.FunctionComponent<Props> = ({
  description,
  dropdownFilters,
  layout,
  list,
  searchInput,
  searchOptions,
  showingTemplate,
  title,
}) => {
  const [filteredLength, setFilteredLength] = useState<number>();
  const [visibleLength, setVisibleLength] = useState<number>();
  const [searchResult, setSearchResult] = useState(list.list);

  const countText =
    isNumber(visibleLength) && isNumber(filteredLength) ? (
      <TextInterpolator
        template={showingTemplate}
        tokens={{
          quantity: String(visibleLength),
          total: String(filteredLength),
        }}
      />
    ) : null;

  const search = (searchTerm: string) => {
    if (!searchTerm) {
      setSearchResult(list.list);
      return;
    }

    const searcher = new FuzzySearch(
      list.list,
      searchOptions.searchKey,
      searchOptions.options
    );

    const result = searcher.search(searchTerm);
    setSearchResult(result);
  };

  return (
    <Layout {...layout}>
      <div className="link-box-list-page">
        <div className="link-box-list-page__container">
          <form>
            <section className="link-box-list-page__section--dark">
              <ContentContainer theme={ContentContainer.themes.wide}>
                <div className="link-box-list-page__header">
                  <Text
                    elementName="h1"
                    size={Text.sizes.headline2}
                    theme={Text.themes.headline}
                    italic
                  >
                    {title}
                  </Text>
                  {description ? (
                    <Text elementName="p">{description}</Text>
                  ) : null}
                  <div className="link-box-list-page__search">
                    <Search
                      onChangeFunc={debounce(value => search(value), 300)}
                      theme={Search.themes.floating}
                      inputSize={Search.inputSizes.small}
                      variant={Search.variants.fixed}
                      {...searchInput}
                    />
                  </div>
                </div>
              </ContentContainer>
            </section>
            <section className="link-box-list-page__section--light">
              <ContentContainer theme={ContentContainer.themes.fullToWide}>
                <div className="link-box-list-page__filters-wrapper">
                  <div className="link-box-list-page__filters">
                    {dropdownFilters.map(filter => (
                      <Filter {...filter} key={filter.title} />
                    ))}
                  </div>
                </div>
              </ContentContainer>
            </section>
          </form>
          <section className="link-box-list-page__section">
            <ContentContainer theme={ContentContainer.themes.wide}>
              <div className="link-box-list-page__content">
                <div
                  className="link-box-list-page__count"
                  data-test-visible-length={visibleLength}
                  data-test-filtered-length={filteredLength}
                >
                  <Text>{countText}</Text>
                </div>

                <LinkBoxList
                  onListChange={({ filteredLength, visibleLength }) => {
                    setFilteredLength(filteredLength);
                    setVisibleLength(visibleLength);
                  }}
                  {...list}
                  list={searchResult}
                />
              </div>
            </ContentContainer>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default LinkBoxListPage;
